import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import { compare, compareIndex } from "@/data/compare";

// Renders every /compare/* page from src/data/compare.js, plus the /compare hub.
//
// One component for all of them on purpose. scripts/prerender.cjs can only write
// crawlable body HTML for routes whose content it can read as data at build
// time, so these pages have to stay in the data file — and once the content is
// data, a second hand-built page component per competitor would only be a way
// for the two to drift apart. scripts/seo/render-html.cjs renders the same
// fields in the same order for the no-JavaScript version.

const comparePath = (slug) => `/${slug}`;

const Kicker = ({ children }) => (
  <p className="font-mono-t text-xs uppercase tracking-[0.25em] text-[#5B8CFF] mb-5">{children}</p>
);

const SectionHead = ({ h, lead }) => (
  <Reveal>
    <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white max-w-2xl">{h}</h2>
    {lead && <p className="mt-3 text-neutral-400 max-w-xl">{lead}</p>}
  </Reveal>
);

// Ticks for what we do, a dash for what we don't — the two lists carry opposite
// meanings and should not look identical.
const PointList = ({ items, tone = "yes" }) => (
  <ul className="mt-10 grid md:grid-cols-2 gap-x-10">
    {items.map((item, i) => (
      <Reveal key={item} delay={(i % 2) * 0.06}>
        <li className="flex items-start gap-3 py-4 border-b border-white/10">
          <span
            className={`grid place-items-center w-5 h-5 mt-0.5 shrink-0 ${
              tone === "yes" ? "bg-[#0047FF]" : "bg-neutral-800"
            }`}
          >
            {tone === "yes" ? <Check size={13} className="text-white" /> : <Minus size={13} className="text-neutral-400" />}
          </span>
          <span className="text-sm text-neutral-300 leading-relaxed">{item}</span>
        </li>
      </Reveal>
    ))}
  </ul>
);

function CompareTable({ table }) {
  return (
    <section className="py-20 border-t border-white/10" data-testid="compare-table">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <SectionHead h={table.h} lead={table.lead} />
        <Reveal delay={0.1}>
          <div className="mt-10 overflow-x-auto border border-white/10 bg-white/[0.02]">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-white/10">
                  {table.head.map((h, i) => (
                    <th
                      key={h}
                      scope="col"
                      className={`px-4 py-3 font-mono-t text-[10px] uppercase tracking-widest font-semibold whitespace-nowrap ${
                        i === table.head.length - 1 ? "text-[#5B8CFF]" : "text-neutral-500"
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r) => (
                  <tr key={r[0]} className="border-t border-white/10 align-top">
                    {r.map((cell, i) => (
                      <td
                        key={i}
                        className={`px-4 py-4 leading-relaxed ${
                          i === 0
                            ? "text-white font-medium whitespace-nowrap"
                            : i === r.length - 1
                              ? "text-neutral-200"
                              : "text-neutral-400"
                        }`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function ComparePage({ slug }) {
  const data = compare[slug];
  if (!data) return null;

  const related = (data.related || []).map((s) => [s, compare[s]]).filter(([, p]) => p);

  return (
    <div data-testid={`compare-${slug.replace(/\//g, "-")}`}>
      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><Kicker>{data.category}</Kicker></Reveal>
          <h1 className="font-heading font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-[3.4rem] text-white max-w-4xl">
            <MaskedLines lines={[data.title]} delay={0.1} />
          </h1>
          <Reveal delay={0.25}>
            <p className="mt-6 text-base md:text-lg text-neutral-400 leading-relaxed max-w-2xl">{data.lead}</p>
            {(data.intro || []).map((t) => (
              <p key={t} className="mt-5 text-neutral-400 leading-relaxed max-w-2xl">{t}</p>
            ))}
            {data.note && (
              <p className="mt-8 border-l-2 border-[#0047FF] pl-5 text-sm text-neutral-400 leading-relaxed max-w-2xl">
                {data.note}
              </p>
            )}
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/demo"
                className="group inline-flex items-center gap-2 bg-neutral-100 text-neutral-900 font-bold px-6 py-3.5 hover:bg-white transition-colors duration-300"
                data-testid="compare-cta-demo"
              >
                Book a demo <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold px-6 py-3.5 hover:border-white/50 hover:bg-white/5 transition-colors duration-300"
              >
                View pricing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What's different */}
      {data.different && (
        <section className="py-20 border-t border-white/10" data-testid="compare-different">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <SectionHead h={data.different.h} lead={data.different.lead} />
            <div className="mt-12 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {data.different.items.map((item, i) => (
                <Reveal key={item.title} delay={(i % 2) * 0.06}>
                  <div className="h-full bg-[#0a0a0a] p-7 md:p-9">
                    <span className="font-mono-t text-xs uppercase tracking-widest text-[#0047FF]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-4 font-heading font-medium text-xl md:text-2xl tracking-tight text-white">{item.title}</h3>
                    <p className="mt-3 text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.table && <CompareTable table={data.table} />}

      {/* What we don't do */}
      {data.limits && (
        <section className="py-20 border-t border-white/10 bg-white/[0.02]" data-testid="compare-limits">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <SectionHead h={data.limits.h} lead={data.limits.lead} />
            <PointList items={data.limits.list} tone="no" />
          </div>
        </section>
      )}

      {/* Where they're a better fit */}
      {data.betterFit && (
        <section className="py-20 border-t border-white/10" data-testid="compare-better-fit">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <SectionHead h={data.betterFit.h} lead={data.betterFit.lead} />
            <PointList items={data.betterFit.list} tone="no" />
          </div>
        </section>
      )}

      {/* FAQ */}
      {data.faqs && data.faqs.length > 0 && (
        <section className="py-20 border-t border-white/10" data-testid="compare-faqs">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <SectionHead h="Questions we get asked" />
            <div className="mt-10 border-t border-white/10">
              {data.faqs.map((f, i) => (
                <Reveal key={f.q} delay={(i % 3) * 0.05}>
                  <div className="grid md:grid-cols-[1fr_1.4fr] gap-4 md:gap-10 py-7 border-b border-white/10">
                    <h3 className="font-heading font-medium text-lg text-white">{f.q}</h3>
                    <p className="text-neutral-400 leading-relaxed">{f.a}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related comparisons */}
      {related.length > 0 && (
        <section className="py-20 border-t border-white/10" data-testid="compare-related">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <SectionHead h="Other comparisons" />
            <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {related.map(([s, p]) => (
                <Link
                  key={s}
                  to={comparePath(s)}
                  className="group bg-[#0a0a0a] p-7 hover:bg-white/[0.04] transition-colors duration-300"
                >
                  <span className="font-mono-t text-[10px] uppercase tracking-widest text-neutral-500">{p.category}</span>
                  <h3 className="mt-3 font-heading font-medium text-lg text-white group-hover:text-[#5B8CFF] transition-colors">
                    {p.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA band */}
      <section className="py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-white max-w-xl">
            Put it on a real call before you decide.
          </h2>
          <Link
            to="/demo"
            className="group inline-flex items-center gap-2 bg-white text-black font-bold px-7 py-4 hover:bg-neutral-200 transition-colors duration-300 shrink-0"
            data-testid="compare-cta-band"
          >
            Book a demo <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}

// The /compare hub: every comparison page, grouped.
export function CompareIndexPage() {
  return (
    <div data-testid="compare-index">
      <section className="relative pt-32 md:pt-40 pb-16 overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
          <Reveal><Kicker>{compareIndex.category}</Kicker></Reveal>
          <h1 className="font-heading font-medium tracking-tight leading-[1.02] text-4xl md:text-5xl lg:text-6xl text-white max-w-3xl">
            <MaskedLines lines={[compareIndex.title]} delay={0.1} />
          </h1>
          <Reveal delay={0.25}>
            <p className="mt-6 text-base md:text-lg text-neutral-400 leading-relaxed max-w-2xl">{compareIndex.lead}</p>
            {compareIndex.intro.map((t) => (
              <p key={t} className="mt-5 text-neutral-400 leading-relaxed max-w-2xl">{t}</p>
            ))}
          </Reveal>
        </div>
      </section>

      {compareIndex.groups.map((g) => (
        <section key={g.h} className="py-16 border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-12">
            <Reveal>
              <h2 className="font-heading font-medium tracking-tight text-2xl md:text-3xl text-white">{g.h}</h2>
            </Reveal>
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {g.slugs
                .filter((s) => compare[s])
                .map((s, i) => (
                  <Reveal key={s} delay={(i % 3) * 0.06}>
                    <Link
                      to={comparePath(s)}
                      className="group h-full block bg-[#0a0a0a] p-7 hover:bg-white/[0.04] transition-colors duration-300"
                    >
                      <h3 className="font-heading font-medium text-lg text-white group-hover:text-[#5B8CFF] transition-colors">
                        {compare[s].title}
                      </h3>
                      <p className="mt-3 text-sm text-neutral-500 leading-relaxed">{compare[s].lead}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 font-mono-t text-[11px] uppercase tracking-widest text-[#5B8CFF]">
                        Read <ArrowUpRight size={13} />
                      </span>
                    </Link>
                  </Reveal>
                ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-24 border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-white max-w-xl">
            Still deciding? Hear it handle a real call.
          </h2>
          <Link
            to="/demo"
            className="group inline-flex items-center gap-2 bg-white text-black font-bold px-7 py-4 hover:bg-neutral-200 transition-colors duration-300 shrink-0"
          >
            Book a demo <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
